import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { PredefinedMilisecondsTypeEnum } from '../../../lib/enums';
import lang from '../../../lib/language';
import { BasePagedResponse } from '../../../lib/models/common-response-types';
import {
  CompanyLogoAndNameState,
  ETF,
  GapperParamType,
  GapperResponse,
  SectorDetail,
} from '../../../lib/models/fundamental/types';
import { RootState } from '../../../lib/store';
import { loadDiscoverChartData } from '../../../lib/store/common-actions';
import { getCompanyLogoAndName, getETFs, getGappers, getSectorDetail } from '../../../lib/store/fundamental';
import { DEFAULT_PAGE_LIST_SIZE, DEFAULT_SEARCH_STOCK_LIMIT, NO_MORE_PAGES_TO_READ } from '../../../lib/store/fundamental/constants';
import { FundamentalCall, SectorEnum } from '../../../lib/store/fundamental/types';
import { isCallStatusPending } from '../../../lib/util/error-handling/StatusByCallHelpers';
import { manageSubscriptions } from '../../../lib/util/TradingHelpers';
import { TypedObject } from '../../../lib/util/types';

import { BaseTable, GoBackButton, LatestNews } from '../../../components';
import NavButtons from '../../../components/NavigationButtons/NavButtons';
import { WATCH_LIST_TABLE_HEADERS } from '../../../components/WatchList/WatchList.constans';
import SkeletonLoaderRow from '../../../components/WatchList/WatchListTable/SkeletonLoaderRow/SkeletonLoaderRow';
import { useIsMobile } from '../../../hooks';
import { NewOnThePopularGroupsData } from '../PopularGroups.types';

import PopularGroupTableItem from './PopularGroupTableItem';

import styles from './PopularGroupTable.module.scss';

const PopularGroupTable = () => {
  const isMobile = useIsMobile();
  const ETFData = useSelector<RootState, BasePagedResponse<ETF>>((state: RootState) => state.fundamental.ETFData);
  const sectorDetails = useSelector<RootState, SectorDetail>((state: RootState) => state.fundamental.sectorDetails);
  const isGettingETFDetails = useSelector<RootState, boolean>((state: RootState) =>
    isCallStatusPending(FundamentalCall.getETFs, state.fundamental.statusByCall));
  const isGettingSectorDetails = useSelector<RootState, boolean>((state: RootState) =>
    isCallStatusPending(FundamentalCall.getSectorDetails, state.fundamental.statusByCall));
  const isGettingGappers = useSelector<RootState, boolean>((state: RootState) =>
    isCallStatusPending(FundamentalCall.getGappers, state.fundamental.statusByCall));
  const gappersContent = useSelector<RootState, GapperResponse[]>((state: RootState) =>
    state.fundamental.gappersData.content);
  const gappersTotalPages = useSelector<RootState, number>((state: RootState) =>
    state.fundamental.gappersData.totalPages);
  const gappersTotalElements = useSelector<RootState, number>((state: RootState) =>
    state.fundamental.gappersData.totalElements);

  const companyLogoAndName = useSelector<RootState, TypedObject<CompanyLogoAndNameState>>(
    (state: RootState) => state.fundamental.companyLogoAndName,
  );

  const dispatch = useDispatch();
  const [abovePreviousClose, setAbovePreviousClose] = useState<boolean>(true);
  const [gappersPage, setGappersPage] = useState<number>(0);
  const [isDelayedLoading, setIsDelayedLoading] = useState(true);

  const history = useHistory();
  const locationState = history.location.state as { group: NewOnThePopularGroupsData, sector: string };
  const { id, symbolCount } = locationState.group;
  const { sector } = locationState;

  const tableHeaders = useMemo(() => WATCH_LIST_TABLE_HEADERS.map((item: string) => <th key={item}>{item}</th>), []);
  manageSubscriptions('discover/popular-group', null, false);
  manageSubscriptions('discover/hot-gappers');

  const tabs = [
    { text: lang.gapperAboveClose(),
      component: <></>,
    },
    { text: lang.gapperBelowClose(),
      component: <></>,
    },
  ];

  const isLoading = useMemo(() => {
    switch (sector) {
      case SectorEnum.Gappers:
        return isGettingGappers && !gappersContent.length;
      case SectorEnum.ETFs:
        return isGettingETFDetails && !ETFData.content.length;
      default:
        return isGettingSectorDetails && !sectorDetails.content.length;
    }
  }, [sector, isGettingGappers, isGettingETFDetails, isGettingSectorDetails, sectorDetails, ETFData, gappersContent]);

  useEffect(() => {
    if (isLoading) {
      setIsDelayedLoading(true);
      return undefined;
    }

    const timer = setTimeout(() => { setIsDelayedLoading(false); }, PredefinedMilisecondsTypeEnum.oneSecond);
    return () => clearTimeout(timer);
  }, [isLoading]);

  useEffect(() => {
    if (sector === SectorEnum.ETFs) {
      return;
    }

    if (sectorDetails.sectorId === id) {
      const sectorSymbols = sectorDetails.content.slice(-DEFAULT_PAGE_LIST_SIZE).map(el => el.symbol).join(',');
      dispatch(loadDiscoverChartData(sectorSymbols));
    } else {
      dispatch(getSectorDetail(Number(id)));
    }
  }, [dispatch, id, sectorDetails.content, sectorDetails.sectorId, sector]);

  useEffect(() => {
    if (sector === SectorEnum.ETFs) {
      dispatch(getETFs({ size: DEFAULT_SEARCH_STOCK_LIMIT, page: ETFData.nextPage }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sector, dispatch]);

  useEffect(() => {
    if (sector === SectorEnum.ETFs && ETFData) {
      const symbols = ETFData.content.map(stock => stock.symbol);
      dispatch(getCompanyLogoAndName(symbols.join()));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ETFData, sector]);

  useEffect(() => {
    if (sector === SectorEnum.Gappers) {
      const payload:GapperParamType = { abovePreviousClose, page: gappersPage, size: DEFAULT_PAGE_LIST_SIZE };
      dispatch(getGappers(payload));
    }
  }, [dispatch, sector, abovePreviousClose, gappersPage]);

  useEffect(() => {
    if (sector !== SectorEnum.ETFs) {
      return;
    }

    if (!isGettingGappers) {
      const sectorSymbols = gappersContent.map(el => el.symbol).join(',');
      dispatch(loadDiscoverChartData(sectorSymbols));
    }
  }, [gappersContent, dispatch]);// eslint-disable-line react-hooks/exhaustive-deps

  const handleButtonLoadMore = () => {
    if (sector === SectorEnum.Gappers) {
      setGappersPage(gappersPage + 1);
    }
    if (sector === SectorEnum.ETFs) {
      dispatch(getETFs({ size: DEFAULT_SEARCH_STOCK_LIMIT, page: ETFData.nextPage }));
    } else {
      dispatch(getSectorDetail(Number(id)));
    }
  };

  const handleCloseChange = (index: number) => {
    setGappersPage(0);
    setAbovePreviousClose(index === 0);
  };

  return (
    <div className={classNames(styles.wrapperLayout, {
      [styles.wrapperLayoutMob]: isMobile,
      [styles.noMargin]: isMobile,
    })}
    >
      <div className={styles.wrapperFirstSection}>
        {!isMobile && (
          <div className={styles.goBackButton}>
            <GoBackButton />
          </div>
        )}
        <div className={styles.titleWrapper}>
          <div className={isMobile ? styles.titleMobile : styles.title}>{sector}</div>
          <div className={isMobile ? styles.stocksLabelMobile : styles.stocksLabel}>
            { sector === SectorEnum.Gappers
              ? lang.gapperInfo()
              : `(${symbolCount} ${lang.accountBreakdownTableStockLabel()})`}
          </div>
        </div>
        { sector === SectorEnum.Gappers ? (
          <div className={styles.gappersWrapper}>
            <NavButtons tabs={tabs} sendCurrentTabIndex={handleCloseChange} />
            <div className={styles.gappersTable}>
              <BaseTable isDiscoverTable className={isMobile ? styles.tableMobile : styles.table}>
                {!isMobile && <thead><tr>{tableHeaders}</tr></thead>}
                <tbody>
                  {isDelayedLoading ? (
                    <>
                      <SkeletonLoaderRow />
                      <SkeletonLoaderRow />
                      <SkeletonLoaderRow />
                      <SkeletonLoaderRow />
                    </>
                  ) : (
                    gappersContent.map(
                      (item, index) => <PopularGroupTableItem key={index as number} item={item} />,
                    )
                  )}
                </tbody>
              </BaseTable>
              {gappersPage < gappersTotalPages && gappersContent.length !== gappersTotalElements && (
                <div className={styles.viewMore}>
                  <button
                    type="button"
                    onClick={handleButtonLoadMore}
                    className={styles.viewMoreButton}
                    disabled={isDelayedLoading}
                  >
                    {lang.commonLoadMore()}
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className={styles.tableWrapper}>
            <BaseTable isDiscoverTable className={isMobile ? styles.tableMobile : styles.table}>
              {!isMobile && <thead><tr>{tableHeaders}</tr></thead>}
              <tbody>
                {isDelayedLoading ? (
                  <>
                    <SkeletonLoaderRow />
                    <SkeletonLoaderRow />
                    <SkeletonLoaderRow />
                    <SkeletonLoaderRow />
                  </>
                ) : (
                  (sector === SectorEnum.ETFs ? ETFData.content : sectorDetails.content).map((item, index) => {
                    const newItem = { ...item, url: companyLogoAndName[item.symbol]?.url };
                    return <PopularGroupTableItem key={index as number} item={newItem} />;
                  })
                )}
              </tbody>
            </BaseTable>
            {(!(sectorDetails.nextPageToRead === NO_MORE_PAGES_TO_READ
              || ETFData.nextPage === NO_MORE_PAGES_TO_READ)
              && (!isGettingETFDetails && !isGettingSectorDetails && !isGettingGappers)) && (
              <div className={styles.viewMore}>
                <button
                  type="button"
                  onClick={handleButtonLoadMore}
                  className={styles.viewMoreButton}
                  disabled={isDelayedLoading}
                >
                  {lang.commonLoadMore()}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {!isMobile && <div className={styles.wrapperSecondSection}><LatestNews /></div>}
    </div>
  );
};

export default PopularGroupTable;
